import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Heart, LogOut, MessageCircle, Clock, User, CreditCard, BadgeCheck, Camera } from "lucide-react";

export default function Dashboard() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [remaining, setRemaining] = useState("");
  const [payments, setPayments] = useState([]);
  const [matches, setMatches] = useState([]);
  const [verify, setVerify] = useState({ status: "NONE", verified: false, prompt: "", has_photo: false });
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyBusy, setVerifyBusy] = useState(false);

  const loadSession = async () => {
    try { const { data } = await api.get("/session/current"); setSession(data?.status === "NONE" ? null : data); } catch {}
  };
  const loadPayments = async () => {
    try { const { data } = await api.get("/payments/history"); setPayments(data); } catch {}
  };
  const loadMatches = async () => {
    try { const { data } = await api.get("/matches"); setMatches(data); } catch {}
  };
  const loadVerify = async () => {
    try { const { data } = await api.get("/verification/status"); setVerify(data); } catch {}
  };
  const [payCurrent, setPayCurrent] = useState(null);
  const [nextAmount, setNextAmount] = useState(null);
  const loadPayCurrent = async () => {
    try { const { data } = await api.get("/payments/current"); setPayCurrent(data?.status === "NONE" ? null : data); } catch {}
    try { const { data } = await api.get("/payments/next-amount"); setNextAmount(data); } catch {}
  };

  useEffect(() => { loadSession(); loadPayments(); loadMatches(); loadVerify(); loadPayCurrent(); }, []);

  useEffect(() => {
    if (!session) return;
    const t = setInterval(() => {
      const diff = new Date(session.expires_at).getTime() - Date.now();
      if (diff <= 0) { setRemaining("Expired"); loadSession(); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`);
    }, 1000);
    return () => clearInterval(t);
  }, [session]);

  const startSession = async () => {
    try {
      const { data } = await api.post("/session/start");
      setSession(data);
      toast.success("24-hour matching session started");
      nav("/match");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start session"); }
  };

  const startNewEntry = () => nav("/onboarding");

  const payNow = () => nav("/pay");

  const submitVerify = async (file) => {
    if (!file) return;
    setVerifyBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/verification/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Verification submitted. Admin will review it.");
      setVerifyOpen(false);
      loadVerify();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally { setVerifyBusy(false); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-dc-gradient">
      <nav className="flex items-center justify-between p-4 max-w-5xl mx-auto">
        <Link to="/" className="font-display text-lg tracking-widest text-white">DILCONNECT</Link>
        <div className="flex items-center gap-3 text-sm">
          <Link data-testid="nav-match" to="/match" className="text-purple-200 hover:text-white">Discover</Link>
          <Link data-testid="nav-matches" to="/matches" className="text-purple-200 hover:text-white">Matches</Link>
          <Link data-testid="nav-profile" to="/profile" className="text-purple-200 hover:text-white">Profile</Link>
          <button data-testid="nav-logout" onClick={() => { logout(); nav("/"); }} className="text-purple-200 hover:text-white"><LogOut className="w-4 h-4" /></button>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto p-4 space-y-6">
        <div className="glass-strong rounded-3xl p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-purple-200/70">Welcome</div>
              <h1 data-testid="dash-name" className="font-display text-2xl font-extrabold flex items-center gap-2">
                {user.display_name}
                {verify.verified && (
                  <span data-testid="dash-verified-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-500/20 border border-sky-400/40 text-sky-200 text-[10px] font-bold uppercase tracking-widest">
                    <BadgeCheck className="w-3 h-3" /> Verified
                  </span>
                )}
              </h1>
              <div className="mt-2 text-sm text-purple-200/80">Status: <span data-testid="dash-status" className="font-bold text-white">{user.status}</span></div>
            </div>
            {session ? (
              <div className="glass rounded-2xl px-5 py-3 flex items-center gap-3">
                <Clock className="w-5 h-5 text-amber-300" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-amber-300">24-hour session</div>
                  <div data-testid="dash-timer" className="font-mono text-xl font-bold text-amber-200">{remaining || "--:--:--"}</div>
                </div>
              </div>
            ) : null}
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            {(() => {
              // State machine
              const hasActiveSession = !!session;
              // Payment is APPROVED and NOT yet consumed by a session
              const approvedUnused = payCurrent?.status === "APPROVED" && !payCurrent?.session_id;
              // Payment is APPROVED but was already consumed
              const approvedConsumed = payCurrent?.status === "APPROVED" && !!payCurrent?.session_id;
              const amountInr = nextAmount?.amount_inr ?? 199;
              const entryType = nextAmount?.entry_type ?? "initial";

              if (hasActiveSession) {
                return (
                  <Link data-testid="dash-go-match" to="/match" className="btn-cta rounded-xl px-5 py-3 inline-flex items-center gap-2">
                    <Heart className="w-4 h-4" />Open matching
                  </Link>
                );
              }
              if (approvedUnused && user.status === "APPROVED") {
                return (
                  <button data-testid="start-session-btn" onClick={startSession} className="btn-cta rounded-xl px-5 py-3">
                    Start matching session
                  </button>
                );
              }
              if (user.status === "PENDING" && !payCurrent) {
                return (
                  <Link data-testid="dash-onboarding" to="/onboarding" className="btn-cta rounded-xl px-5 py-3">
                    Complete onboarding
                  </Link>
                );
              }
              if (payCurrent?.status === "PENDING") {
                return (
                  <div data-testid="payment-pending-banner" className="text-sm text-amber-300">
                    Payment Status: <b>Pending Verification</b> · <Link to="/pay" className="underline">View</Link>
                  </div>
                );
              }
              if (payCurrent?.status === "REJECTED") {
                return (
                  <div data-testid="payment-rejected-banner" className="text-sm text-rose-300">
                    Payment Status: <b>Rejected</b>{payCurrent.rejection_reason ? ` — ${payCurrent.rejection_reason}` : ""} · <Link to="/pay" className="underline">Try again</Link>
                  </div>
                );
              }
              // No active session + no pending payment:
              // either no payment at all OR the approved one was already consumed (user ended a connection)
              const ctaLabel = approvedConsumed || entryType === "reentry"
                ? `Start New Entry — ₹${amountInr}`
                : `Pay ₹${amountInr} to start`;
              const subLabel = approvedConsumed
                ? "Ready for a new connection?"
                : null;
              return (
                <div className="flex flex-col gap-1">
                  {subLabel && <div className="text-xs text-purple-200/80">{subLabel}</div>}
                  <button data-testid="dash-pay-cta" onClick={payNow} className="btn-cta rounded-xl px-5 py-3">
                    {ctaLabel}
                  </button>
                </div>
              );
            })()}
          </div>

          {/* Verification CTA */}
          {!verify.verified && (
            <div className="mt-6 glass rounded-2xl p-4 flex items-start gap-3">
              <BadgeCheck className="w-5 h-5 text-sky-300 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-bold text-white">Get the Verified badge</div>
                <div className="text-xs text-purple-200/80 mt-0.5">
                  {verify.status === "PENDING"
                    ? "Submitted — pending admin review."
                    : verify.status === "REJECTED"
                    ? "Your previous verification was rejected. You can resubmit."
                    : "Take a quick selfie holding today's date + ✌️ to earn a glowing Verified badge on your profile."}
                </div>
                {verify.status !== "PENDING" && (
                  <button data-testid="start-verify-btn" onClick={() => setVerifyOpen(true)} className="mt-2 text-sm text-sky-300 font-semibold hover:text-sky-200">
                    Start verification →
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="glass rounded-3xl p-5">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold">Your matches</h2>
              <Link to="/matches" className="text-xs text-purple-200 hover:text-white">View all</Link>
            </div>
            {matches.length === 0 ? (
              <div className="text-sm text-purple-200/70 mt-3">No matches yet. Start matching to find mutual connections.</div>
            ) : (
              <ul className="mt-3 space-y-2">
                {matches.slice(0, 3).map((m) => (
                  <li key={m.match.id}>
                    <Link to={`/matches/${m.match.id}`} className="flex items-center justify-between hover:bg-purple-900/30 rounded-xl px-3 py-2">
                      <span className="font-medium">{m.other?.display_name}</span>
                      <MessageCircle className="w-4 h-4 text-purple-300" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="glass rounded-3xl p-5">
            <h2 className="font-display text-lg font-bold flex items-center gap-2"><CreditCard className="w-4 h-4" /> Payment history</h2>
            {payments.length === 0 ? (
              <div className="text-sm text-purple-200/70 mt-3">No payments yet.</div>
            ) : (
              <ul className="mt-3 space-y-2 text-sm">
                {payments.slice(0, 5).map((p) => (
                  <li key={p.id} className="flex items-center justify-between">
                    <span className="text-purple-200/80 font-mono text-xs">{(p.verification_id || p.id).slice(-10)}</span>
                    <span>₹{(p.amount / 100).toFixed(0)}</span>
                    <span className={`text-xs font-semibold ${p.status === "APPROVED" ? "text-emerald-300" : p.status === "REJECTED" ? "text-rose-300" : "text-amber-300"}`}>{p.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </main>

      {verifyOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="glass-strong rounded-3xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-sky-300" />
              <h3 className="font-display text-xl font-bold">Get Verified</h3>
            </div>
            <p className="text-sm text-purple-200/80 mt-3">
              Take a selfie with: <b className="text-white">{verify.prompt || "Show today's date on paper + peace sign"}</b>
            </p>
            <p className="text-[11px] text-purple-200/60 mt-1">Admin will review within 24 hours. The photo is private.</p>
            <label className="mt-4 block">
              <span className="btn-cta inline-block w-full text-center py-2.5 rounded-xl cursor-pointer">
                {verifyBusy ? "Uploading…" : "Choose selfie"}
              </span>
              <input data-testid="verify-upload" type="file" accept="image/*" className="hidden" onChange={(e) => submitVerify(e.target.files?.[0])} />
            </label>
            <button onClick={() => setVerifyOpen(false)} className="mt-3 w-full py-2 rounded-xl bg-purple-900/40 text-purple-200">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
